import type {
    Cycle,
    MealType,
    MeasurableTrackableType,
    Mood,
    ScaleType,
    SexType,
    TrackableData,
    TrackableMeasure,
    TrackableType,
    WaterVolume,
} from "@qendo/trackables";
import type { ActivityOptionType, SymptomOptionType } from "@qendo/types";
import type { DateArg } from "date-fns";
import type { Dispatch, SetStateAction } from "react";
import type { OptionalNumeric } from "~/util/string";

export type OneToTen = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10;
export type OneToTwenty = OneToTen | 11 | 12 | 13 | 14 | 15 | 16 | 17 | 18 | 19 | 20;
export type OneToThirty = OneToTwenty | 21 | 22 | 23 | 24 | 25 | 26 | 27 | 28 | 29 | 30;

export const DEFAULT_TEMP = 37.0;
export const MIN_TEMP = 35.0;
export const MAX_TEMP = 41.0;

export type BaseScaleData<S extends ScaleType> = {
    type: S;
};
export type BooleanScaleData = {
    value: boolean;
} & BaseScaleData<ScaleType.boolean>;

export type CycleScaleData = {
    value: Cycle | null;
} & BaseScaleData<ScaleType.cycle>;

export type MealScaleData = {
    value: MealType | null;
} & BaseScaleData<ScaleType.meal>;

export type MoodScaleData = {
    value: Mood | null;
    feelings: string[];
} & BaseScaleData<ScaleType.mood>;

export type NoneScaleData = {
    value?: never;
} & BaseScaleData<ScaleType.none>;

export type OvulationScaleData = {
    value: OneToTen | null;
    mucus: boolean;
    basal: boolean;
    breast: boolean;
    headache: boolean;
} & BaseScaleData<ScaleType.ovulation>;

export type SexScaleData = {
    value: SexType | null;
} & BaseScaleData<ScaleType.sex>;

export type SleepScaleData = {
    value: OneToTen | null;
    start: DateArg<Date> | null;
    end: DateArg<Date> | null;
} & BaseScaleData<ScaleType.sleep>;

export type TemperatureScaleData = {
    value: number;
} & BaseScaleData<ScaleType.temperature>;

export type TenScaleData = {
    value: OneToTen | null;
} & BaseScaleData<ScaleType.ten>;

export type TwentyScaleData = {
    value: OneToTwenty | null;
} & BaseScaleData<ScaleType.twenty>;

export type ThirtyScaleData = {
    value: OneToThirty | null;
} & BaseScaleData<ScaleType.thirty>;

export type WaterScaleData = {
    value: OptionalNumeric;
    volume: WaterVolume;
} & BaseScaleData<ScaleType.water>;

export type ScaleData<S extends ScaleType> = S extends ScaleType.boolean
    ? BooleanScaleData
    : S extends ScaleType.cycle
      ? CycleScaleData
      : S extends ScaleType.meal
        ? MealScaleData
        : S extends ScaleType.mood
          ? MoodScaleData
          : S extends ScaleType.none
            ? NoneScaleData
            : S extends ScaleType.ovulation
              ? OvulationScaleData
              : S extends ScaleType.sex
                ? SexScaleData
                : S extends ScaleType.sleep
                  ? SleepScaleData
                  : S extends ScaleType.temperature
                    ? TemperatureScaleData
                    : S extends ScaleType.ten
                      ? TenScaleData
                      : S extends ScaleType.twenty
                        ? TwentyScaleData
                        : S extends ScaleType.thirty
                          ? ThirtyScaleData
                          : S extends ScaleType.water
                            ? WaterScaleData
                            : never;

export type SetScaleValue<S extends ScaleType> =
    | ((value: ScaleData<S>) => void)
    | Dispatch<SetStateAction<ScaleData<S>>>;

export type ScaleSelectorProps<S extends ScaleType> = {
    readonly label?: string;
    value: ScaleData<S>;
    readonly onUpdateScale: SetScaleValue<S>;
    error?: string | null;
    disabled?: boolean;
    zIndex?: number;
};

export type ActivityMeasureRecord = { [key in ActivityOptionType]?: string | null };
export type SymptomMeasureRecord = { [key in SymptomOptionType]?: string | null };
export type JournalMeasureRecord = { [key in TrackableMeasure]?: never };
export type MeasureRecord<T extends TrackableType = MeasurableTrackableType> = {
    [key in TrackableMeasure<T & MeasurableTrackableType>]?: string | null;
} & (ActivityMeasureRecord | SymptomMeasureRecord | JournalMeasureRecord);

export interface BaseJournalData<T extends TrackableType, S extends ScaleType, D extends TrackableData<T, S>> {
    id: string;
    trackable_id: D["id"];
    search?: Lowercase<string>[] | null;
    scale: ScaleData<S & D["scale"]>;
    description: string | null;
    author_id?: string | null;
    author_name?: string | null;
    photograph_ids?: string[];
    recorded_on: string;
    recorded_at: string;
    flag?: boolean;
    flare?: boolean;
    measures?: MeasureRecord<T>;
    treatment_ids?: string[] | undefined;
}

export interface ActivityJournalData<S extends ScaleType = ScaleType.boolean>
    extends BaseJournalData<TrackableType.activity, S, TrackableData<TrackableType.activity, S>> {
    measures?: ActivityMeasureRecord;
    treatment_ids?: never;
}
export interface SymptomJournalData<S extends ScaleType = ScaleType.ten>
    extends BaseJournalData<TrackableType.symptom, S, TrackableData<TrackableType.symptom, S>> {
    measures?: SymptomMeasureRecord;
    treatment_ids?: string[];
}
export interface OtherJournalData<S extends ScaleType = ScaleType.none>
    extends BaseJournalData<TrackableType.journal, S, TrackableData<TrackableType.journal, S>> {
    measures?: never;
    treatment_ids?: never;
}

export type JournalData<T extends TrackableType = TrackableType, S extends ScaleType = ScaleType> = (
    | ActivityJournalData<S>
    | SymptomJournalData<S>
    | OtherJournalData<S>
) &
    BaseJournalData<T, S, TrackableData<T, S>>;

export interface BaseJournalPayload<S extends ScaleType = ScaleType, T extends TrackableType = TrackableType> {
    type: T;
    flare: boolean;
    scale: ScaleData<S>;
    recorded_at: DateArg<Date>;
    description?: string | null;
    photograph_ids?: string[];
    measures?: MeasureRecord<T>;
}

export interface UnmeasurableJournalPayload<S extends ScaleType = ScaleType, T extends TrackableType = TrackableType>
    extends BaseJournalPayload<S, T> {
    measures?: never;
}

export interface MeasurableActivityPayload<S extends ScaleType = ScaleType>
    extends BaseJournalPayload<S, TrackableType.activity> {
    measures: ActivityMeasureRecord;
}

export interface MeasurableSymptomPayload<S extends ScaleType = ScaleType>
    extends BaseJournalPayload<S, TrackableType.symptom> {
    measures: SymptomMeasureRecord;
}

export type SymptomJournalPayload<S extends ScaleType = ScaleType> = {
    flag?: boolean | null;
    treatments?: string[];
} & (UnmeasurableJournalPayload<S, TrackableType.symptom> | MeasurableSymptomPayload<S>);
export type JournalUpdatePayload<S extends ScaleType = ScaleType, T extends TrackableType = TrackableType> = {
    id: string;
    trackable_id?: never;
} & (UnmeasurableJournalPayload<S, T> | MeasurableActivityPayload<S> | SymptomJournalPayload<S>);
export type JournalCreatePayload<S extends ScaleType = ScaleType, T extends TrackableType = TrackableType> = {
    id?: never;
    trackable_id: string;
} & (UnmeasurableJournalPayload<S, T> | MeasurableActivityPayload<S> | SymptomJournalPayload<S>);
export type JournalPayload<S extends ScaleType = ScaleType, T extends TrackableType = TrackableType> =
    | JournalUpdatePayload<S, T>
    | JournalCreatePayload<S, T>;
export type PeriodPayload = {
    id?: string;
    recorded_at: DateArg<Date>;
    status: Cycle;
};
